/**
 * Utility functions for replacing variables in text templates
 */

import { formatAmount } from "@/utils/numberFormat";

/**
 * Replace variables in a text template with actual values
 * @param {string} template - The text template containing variables like {{variable_name}}
 * @param {Object} data - Object containing the data to replace variables with
 * @returns {string} - The text with variables replaced
 */
export function replaceVariables(template, data) {
    if (!template || typeof template !== 'string') {
        return '';
    }

    return template.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
        const trimmedVariable = variableName.trim();
        const value = getNestedValue(data, trimmedVariable);
        return value !== undefined ? value : match; // Keep original if not found
    });
}

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - The object to get value from
 * @param {string} path - The path to the value (e.g., 'customer.name')
 * @returns {*} - The value or undefined if not found
 */
function getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
}

/**
 * Format currency value
 * @param {number} amount - The amount to format
 * @param {string} currency - The currency code (e.g., 'EUR')
 * @returns {string} - Formatted currency string
 */
export function formatCurrency(amount, currency = 'EUR') {
    if (amount === undefined || amount === null) {
        return '';
    }
    try {
        return formatAmount(amount, {
            currency: currency || 'EUR',
            forceDecimals: true,
        });
    } catch (error) {
        return `${amount} ${currency}`;
    }
}

/**
 * Format date value
 * @param {string|Date} date - The date to format
 * @param {string} locale - The locale for formatting (default: 'fr-FR')
 * @returns {string} - Formatted date string
 */
export function formatDate(date, locale = 'fr-FR') {
    if (!date) {
        return '';
    }

    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return new Intl.DateTimeFormat(locale).format(dateObj);
    } catch (error) {
        return date.toString();
    }
}

/**
 * Calculate days overdue
 * @param {string|Date} dueDate - The due date of the invoice
 * @returns {number} - Number of days overdue (0 if not overdue)
 */
export function calculateDaysDiff(dueDate) {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
}

/**
 * Get currency symbol
 * @param {string} currency - The currency code (e.g., 'EUR')
 * @returns {string} - The currency symbol
 */
export function getCurrencySymbol(currency) {
    const symbols = {
        EUR: "€",
        USD: "$",
        GBP: "£",
        CHF: "CHF",
    };
    return symbols[currency] || currency;
}

/*
 * Get phone numbers
 * @param {Object} address - The address object
 * @returns {string} - The phone numbers
 */
export function getPhoneNumbers(address) {
    if (!address?.phone_number && !address?.mobile_phone_number) {
        return null;
    }
    let phone_numbers = [];
    if (address.phone_number && address.phone_number.length) {
        phone_numbers.push(address.phone_number);
    }
    if (address.mobile_phone_number && address.mobile_phone_number.length) {
        phone_numbers.push(address.mobile_phone_number);
    }
    return phone_numbers.join(" / ");
}

export function formatDateLong(date) {
    if (!date) return '';
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return new Intl.DateTimeFormat('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(dateObj);
    } catch (error) {
        return date.toString();
    }
}